import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Calendar, X, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * BookingSlotPicker — visual booking calendar for clients.
 * Shows the next 14 days; for the picked day, every hourly slot (6am–8pm)
 * is marked available / taken / blocked / past, live from the coach's
 * shared calendar. Clients can only tap OPEN slots — no more typing a
 * date into a blind datetime field.
 */

const HOURS = Array.from({ length: 15 }, (_, i) => 6 + i); // 6am..8pm
const fmtHour = (h) => (h > 12 ? `${h - 12}:00 PM` : h === 12 ? "12:00 PM" : `${h}:00 AM`);

function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export default function BookingSlotPicker({ onBooked, onClose }) {
  const [days] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: 14 }, (_, i) => addDays(today, i));
  });
  const [dayIdx, setDayIdx] = useState(0);
  const [avail, setAvail] = useState(null); // { busy: [], blocks: [] }
  const [selected, setSelected] = useState(null); // Date of chosen slot
  const [sessionType, setSessionType] = useState("in_person");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("/sessions/availability", {
          params: { start: days[0].toISOString(), days: 14 },
        });
        setAvail(r.data);
      } catch {
        setAvail({ busy: [], blocks: [] });
      }
    };
    load();
  }, [days]);

  // Precompute intervals as Date pairs once.
  const intervals = useMemo(() => {
    if (!avail) return { busy: [], blocks: [] };
    const toPair = (x) => ({ ...x, s: new Date(x.start_at), e: new Date(x.end_at) });
    return { busy: (avail.busy || []).map(toPair), blocks: (avail.blocks || []).map(toPair) };
  }, [avail]);

  const slotStatus = (slotStart) => {
    const slotEnd = new Date(slotStart.getTime() + 60 * 60000);
    if (slotStart < new Date()) return "past";
    const block = intervals.blocks.find((b) => b.s < slotEnd && b.e > slotStart);
    if (block) return "blocked";
    const busy = intervals.busy.find((b) => b.s < slotEnd && b.e > slotStart);
    if (busy) return busy.mine ? "mine" : "taken";
    return "open";
  };

  const day = days[dayIdx];
  const slots = HOURS.map((h) => {
    const s = new Date(day);
    s.setHours(h, 0, 0, 0);
    return { hour: h, start: s, status: slotStatus(s) };
  });
  const openCount = slots.filter((s) => s.status === "open").length;

  const book = async () => {
    if (!selected) return toast.error("Pick an open time slot");
    setSaving(true);
    try {
      await api.post("/sessions/book", {
        scheduled_at: selected.toISOString(),
        duration_min: 60,
        session_type: sessionType,
        note: note || null,
      });
      toast.success("Session booked — you'll hear from your coach");
      onBooked?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4"
      onClick={onClose}
      data-testid="session-book-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="obs-card !p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="h-5 w-5 text-blaze" />
          <h3 className="font-heading text-2xl uppercase flex-1">Book a session</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="booking-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Session type: in the gym or over video */}
        <div className="flex gap-2 mb-3" data-testid="booking-type-toggle">
          {[
            { v: "in_person", label: "In person" },
            { v: "online", label: "Online video" },
          ].map(({ v, label }) => (
            <button
              key={v}
              type="button"
              data-testid={`booking-type-${v}`}
              onClick={() => setSessionType(v)}
              className={`flex-1 py-2 rounded-sm border text-[11px] uppercase tracking-widest transition-colors ${
                sessionType === v
                  ? "border-blaze bg-blaze/15 text-blaze font-bold"
                  : "border-obsidian-600 text-zinc-400 hover:border-blaze/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Day strip */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3 -mx-1 px-1">
          {days.map((d, i) => (
            <button
              key={i}
              type="button"
              data-testid={`booking-day-${i}`}
              onClick={() => {
                setDayIdx(i);
                setSelected(null);
              }}
              className={`shrink-0 w-14 py-2 rounded-sm border text-center transition-colors ${
                i === dayIdx ? "border-blaze bg-blaze/15 text-blaze" : "border-obsidian-600 text-zinc-400 hover:border-blaze/60"
              }`}
            >
              <div className="text-[9px] uppercase tracking-widest">
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className="font-heading text-lg leading-tight">{d.getDate()}</div>
              <div className="text-[9px] text-zinc-500">{d.toLocaleDateString(undefined, { month: "short" })}</div>
            </button>
          ))}
        </div>

        {/* Slot grid */}
        {!avail ? (
          <p className="text-xs text-zinc-500 py-6 text-center">Loading availability…</p>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2" data-testid="booking-open-count">
              {openCount} open slot{openCount === 1 ? "" : "s"} · {day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <div className="grid grid-cols-3 gap-1.5 mb-4">
              {slots.map(({ hour, start, status }) => {
                const isSel = selected && start.getTime() === selected.getTime();
                const base = "py-2.5 rounded-sm border text-xs text-center transition-colors";
                if (status === "open") {
                  return (
                    <button
                      key={hour}
                      type="button"
                      data-testid={`booking-slot-${hour}`}
                      onClick={() => setSelected(isSel ? null : start)}
                      className={`${base} ${
                        isSel ? "border-blaze bg-blaze text-obsidian-900 font-bold" : "border-blaze/40 text-zinc-200 hover:bg-blaze/15"
                      }`}
                    >
                      {isSel && <Check className="h-3 w-3 inline mr-1" />}
                      {fmtHour(hour)}
                    </button>
                  );
                }
                const label = status === "mine" ? "Yours" : status === "taken" ? "Taken" : status === "blocked" ? "Blocked" : fmtHour(hour);
                return (
                  <div
                    key={hour}
                    data-testid={`booking-slot-${hour}`}
                    className={`${base} border-obsidian-700 text-zinc-600 bg-obsidian-900/60 cursor-not-allowed ${
                      status === "mine" ? "!text-blaze/70 !border-blaze/30" : ""
                    }`}
                    title={status === "past" ? "In the past" : label}
                  >
                    {status === "past" ? <span className="line-through">{fmtHour(hour)}</span> : `${fmtHour(hour)} · ${label}`}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <label className="block mb-3">
          <div className="overline mb-1">Note to coach (optional)</div>
          <textarea
            rows={2}
            data-testid="session-book-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Focus areas, injuries, location prefs…"
            className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
          />
        </label>
        <p className="text-[11px] text-zinc-500 uppercase tracking-widest mb-4">
          Reminder — cancellations don&apos;t come with a refund. Session still counts against your package.
        </p>
        <button
          type="button"
          data-testid="session-book-submit"
          onClick={book}
          disabled={saving || !selected}
          className="w-full blaze-btn py-3 text-xs uppercase tracking-widest disabled:opacity-50"
        >
          {saving
            ? "Booking…"
            : selected
            ? `Book ${selected.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${selected.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
            : "Pick an open slot above"}
        </button>
      </div>
    </div>
  );
}
